import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { Start, PoolBetting, Coupon, Syndicates, Help } from '../images/gogoicons';
import { IsOrangeLight, themeC } from '../theme/themehelper';

interface TabItem {
    id: string;
    label: string;
    icon: React.ReactNode;
    to: string;
    eventId: string;
}

function MobileNavBarGoGo({ realMode, agentSyndicateBetting, reportSelection, whichToUnderline, theme }: {
    realMode: boolean;
    agentSyndicateBetting: boolean;
    whichToUnderline: string;
    reportSelection: (type: string, id: string) => void;
    theme?: string;
}) {
    const isOrange = theme === themeC.Orange;
    const isOrangeLight = IsOrangeLight(theme ?? '');
    const accentColor = theme === themeC.Lion ? '#FD5C3C' : theme === themeC.Wood ? '#B19661' : isOrange ? '#FE8000' : '#F14AA7';
    const navFont = theme === themeC.Wood ? 'GT-America' : isOrange ? 'Inter' : 'Inter';
    const menuId = theme === themeC.Lion ? 'lion_menu' : theme === themeC.Wood ? 'wood_menu' : isOrange ? 'orange_menu' : 'gogo_menu';
    const navBackground = isOrangeLight ? '#FFFFFF' : '#121212';
    const navBorder = isOrangeLight ? '1px solid #EBEBEB' : '1px solid #575757';
    const activeLabelColor = isOrangeLight ? '#1F1F1F' : '#F0F0F0';
    const inactiveLabelColor = isOrangeLight ? '#636363' : '#B3B3B3';
    const focusRingColor = isOrangeLight ? 'rgba(11, 18, 23, 0.36)' : 'rgba(246, 243, 239, 0.36)';
    const { t } = useTranslation();
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const [focusedId, setFocusedId] = useState<string | null>(null);
    const isMobile = useMediaQuery('(max-width: 480px)');

    const tabs: (TabItem | null)[] = [
        { id: '0', label: 'Start', icon: <Start />, to: '/clear', eventId: 'home' },
        { id: '4', label: t('rounds'), icon: <PoolBetting />, to: '/roundhistory', eventId: 'rounds' },
        realMode ? { id: '3', label: t('my_coupons'), icon: <Coupon />, to: '/mycoupons', eventId: 'coupons' } : null,
        agentSyndicateBetting ? { id: '2', label: t('bet_with_syndicates'), icon: <Syndicates />, to: '/playagent', eventId: 'syndicates' } : null,
        { id: '6', label: t('help'), icon: <Help />, to: '/help', eventId: 'help' },
    ];

    return (
        <nav style={{
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'flex-end',
            padding: isOrange ? '0px 12px' : undefined,
            width: '100%',
            height: '40px',
            overflowX: 'auto',
            background: theme === themeC.Lion ? '#FFFFFF' : navBackground,
            borderBottom: theme === themeC.Lion ? '1px solid #DDDDDD' : navBorder,
            scrollbarWidth: 'none',
        }}>
            {tabs.filter(Boolean).map((tab) => {
                const isActive = whichToUnderline === tab!.id;
                const isHovered = hoveredId === tab!.id;
                const isFocused = focusedId === tab!.id;
                const showUnderline = isActive || isHovered;
                const labelColor = theme === themeC.Lion
                    ? (isActive ? '#1F1F1F' : '#636363')
                    : (isActive ? activeLabelColor : inactiveLabelColor);

                return (
                    <Link
                        key={tab!.id}
                        to={tab!.to}
                        style={{ textDecoration: 'none', flexGrow: 1, outline: 'none' }}
                        onClick={() => reportSelection(tab!.eventId, menuId)}
                        onFocus={() => setFocusedId(tab!.id)}
                        onBlur={() => setFocusedId(null)}
                    >
                        <div
                            style={{
                                position: 'relative',
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: isMobile ? '8px 6px' : '8px 16px',
                                gap: '4px',
                                height: '40px',
                                minHeight: '40px',
                                boxSizing: 'border-box',
                                borderBottom: showUnderline ? `2px solid ${accentColor}` : '2px solid transparent',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                outline: isFocused ? `2px solid ${theme === themeC.Lion ? 'rgba(11, 18, 23, 0.36)' : 'rgba(246, 243, 239, 0.36)'}` : 'none',
                                outlineOffset: '2px',
                            }}
                            onMouseEnter={() => setHoveredId(tab!.id)}
                            onMouseLeave={() => setHoveredId(null)}
                        >
                            <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexShrink: 0, color: labelColor,
                            }}>
                                <span className={theme === themeC.Lion ? (isActive ? 'lion-icon-active' : 'lion-icon-inactive') : ''} style={{ display: 'flex' }}>
                                    {tab!.icon}
                                </span>
                            </span>
                            {!isMobile && (
                            <span style={{
                                fontFamily: navFont,
                                fontStyle: 'normal',
                                fontWeight: 700,
                                fontSize: '12px',
                                lineHeight: '12px',
                                letterSpacing: '0.5px',
                                color: labelColor,
                                textTransform: isOrange ? 'none' : 'uppercase',
                            }}>
                                {tab!.label}
                            </span>
                            )}
                        </div>
                    </Link>
                );
            })}
        </nav>
    );
}

export default MobileNavBarGoGo;
